import {createApi} from '@reduxjs/toolkit/query/react'
import {baseQueryWithReAuth} from "@/store/features/baseRtkQuery";

export interface ToolExecutionParameter {
    name: string;
    value: any;
    type?: string;
    description?: string;
    isRequired?: boolean;
}

export interface ToolExecutionRequest {
    parameters: ToolExecutionParameter[];
}

export interface ToolExecutionResponse {
    success: boolean;
    result?: any;
    error?: string;
    toolName: string;
    toolType?: string;
}

export interface ToolSummary {
    toolName: string;
    name: string;
    version: string;
    description: string;
    category: string;
    interface?: string;
    canExecute: boolean;
    toolType: string;
}

export interface GetAllToolsResponse {
    tools: ToolSummary[];
}

export interface ParameterInfo {
    name: string;
    type: string;
    description: string;
    isRequired: boolean;
}

export interface ToolMetadataResponse {
    toolName: string;
    description: string;
    version: string;
    category: string;
    toolType: string;
    parameters: ParameterInfo[];
    returnType: string;
}

export const toolExecutionApiSlice = createApi({
    reducerPath: 'toolExecutionApi',
    baseQuery: baseQueryWithReAuth,
    endpoints: (builder) => ({
        // Endpoint 1: GET /Tools - Gets all available tools
        getAllTools: builder.query<GetAllToolsResponse, void>({
            query: () => ({
                url: 'Tools',
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
            }),
        }),
        
        // Endpoint 2: POST /Tools/{toolName}/execute - Executes a tool
        executeTool: builder.mutation<ToolExecutionResponse, { toolName: string; request: ToolExecutionRequest }>({
            query: ({ toolName, request }) => ({
                url: `Tools/${encodeURIComponent(toolName)}/execute`,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: request,
            }),
        }),
        
        // Endpoint 3: GET /Tools/{toolName}/metadata - Gets tool metadata
        getToolMetadata: builder.query<ToolMetadataResponse, string>({
            query: (toolName) => ({
                url: `Tools/${encodeURIComponent(toolName)}/metadata`,
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
            }),
        }),
    }),
})

const {
    useGetAllToolsQuery,
    useExecuteToolMutation,
    useGetToolMetadataQuery,
} = toolExecutionApiSlice

export const toolExecutionApiActions = {
    useGetAllToolsQuery,
    useExecuteToolMutation,
    useGetToolMetadataQuery,
}
